import React from 'react';

const ProgressBar = ({ currentStep, totalSteps = 6 }) => {
  const steps = [
    { number: 1, title: 'Idea Input', description: 'Share your business idea', icon: 'lightbulb' },
    { number: 2, title: 'AI Discovery', description: 'AI conversation to refine focus', icon: 'chat' },
    { number: 3, title: 'Market Focus', description: 'Confirm your core market', icon: 'eye' },
    { number: 4, title: 'Select Categories', description: 'Choose market segments', icon: 'target' },
    { number: 5, title: 'Real Pain Points', description: 'Get pain points via Reddit', icon: 'reddit' },
    { number: 6, title: 'Generate Report', description: 'Create insights report', icon: 'document' }
  ];

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-primary-600 text-white border-primary-600';
      case 'current':
        return 'bg-primary-100 text-primary-600 border-primary-600 ring-2 ring-primary-200';
      case 'upcoming':
        return 'bg-gray-100 text-gray-400 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-400 border-gray-300';
    }
  };

  const getConnectorClasses = (stepNumber) => {
    return stepNumber < currentStep ? 'bg-primary-600' : 'bg-gray-300';
  };

  const renderStepIcon = (iconType, status) => {
    const iconClass = "w-6 h-6";
    
    switch (iconType) {
      case 'lightbulb':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'chat':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'eye':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        );
      case 'target':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="1" strokeWidth={2}/>
            <circle cx="12" cy="12" r="5" strokeWidth={2}/>
            <circle cx="12" cy="12" r="9" strokeWidth={2}/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v2M12 19v2M3 12h2M19 12h2"/>
          </svg>
        );
      case 'reddit':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h1a4 4 0 110 8h-1m-10-8H6a4 4 0 100 8h1m3-4h4m-6 4h8" />
          </svg>
        );
      case 'document':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return status === 'completed' ? (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : null;
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const status = getStepStatus(step.number);
            const stepClasses = getStepClasses(status);
            const showConnector = index < steps.length - 1;

            return (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1 min-w-0">
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold text-sm transition-all duration-200 ${stepClasses}`}
                  >
                    {status === 'completed' ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      renderStepIcon(step.icon, status)
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div className={`text-sm font-medium ${status === 'current' ? 'text-primary-600' : status === 'completed' ? 'text-gray-900' : 'text-gray-400'}`}>
                      {step.title}
                    </div>
                    <div className={`text-xs mt-1 font-medium ${status === 'current' ? 'text-primary-500' : status === 'completed' ? 'text-gray-600' : 'text-gray-400'}`}>
                      {step.number} of {totalSteps}
                    </div>
                    <div className={`text-xs mt-1 hidden sm:block ${status === 'current' ? 'text-primary-500' : status === 'completed' ? 'text-gray-600' : 'text-gray-400'}`}>
                      {step.description}
                    </div>
                  </div>
                </div>
                
                {showConnector && (
                  <div className="flex-1 flex items-center justify-center px-2">
                    <div className={`h-0.5 w-full transition-all duration-200 ${getConnectorClasses(step.number)}`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Mobile-friendly progress description */}
        <div className="mt-4 text-center sm:hidden">
          <div className="text-sm text-gray-600">
            Step {currentStep} of {totalSteps}: {steps.find(s => s.number === currentStep)?.title}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {steps.find(s => s.number === currentStep)?.description}
          </div>
        </div>

        {/* Progress bar for smaller screens */}
        <div className="mt-4 block sm:hidden">
          <div className="bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-primary-600 to-sage-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Start</span>
            <span>Complete</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;